package fr.frux.navigation

import androidx.navigation3.runtime.NavKey
import fr.frux.shared.domain.model.HarvestTarget
import kotlinx.serialization.Serializable

/**
 * Geste qui a ouvert le formulaire d'ajout (spec onboarding de contribution §3). Porté par la
 * route pour adapter la confirmation et, au lot 4, mesurer le parcours. [TREE_INVITE] est réservé
 * à l'invitation depuis une fiche existante (lot 3), sans appelant à ce jour.
 */
@Serializable
enum class AddTreeOrigin {
    EMPTY_AREA,
    MAP_FAB,
    TREE_INVITE,
    UNKNOWN,
}

/**
 * Type-safe navigation routes for Frux.
 * All routes MUST be @Serializable and implement NavKey for Navigation 3 UI.
 *
 * Navigation 3 UI features:
 * - Developer-owned back stack (direct control via NavBackStack)
 * - Type-safe routes with compile-time checks
 * - Automatic state persistence across process death
 * - Works on all platforms: Android, iOS, Desktop, Web
 *
 * References:
 * - https://kotlinlang.org/docs/multiplatform/compose-navigation-3.html
 * - https://developer.android.com/guide/navigation/navigation-3
 */
@Serializable
sealed interface Route : NavKey {
    /**
     * Welcome/onboarding screen (first launch)
     */
    @Serializable
    data object Welcome : Route

    /**
     * Authentication screen (login/register)
     */
    @Serializable
    data object Auth : Route

    /**
     * Main map screen showing nearby fruit trees (Explore tab)
     */
    @Serializable
    data object Map : Route

    /**
     * Récoltes — récoltes en cours et activité
     */
    @Serializable
    data object Harvests : Route

    /**
     * Mes arbres — arbres créés par l'utilisateur. Sous-page de Profil depuis la
     * restructuration de la barre du bas (spec 2026-08-06), plus un onglet.
     */
    @Serializable
    data object MyTrees : Route

    /**
     * Favoris — arbres suivis par l'utilisateur, point de retour terrain et socle des futures
     * alertes de floraison.
     */
    @Serializable
    data object Favorites : Route

    /**
     * Settings screen
     */
    @Serializable
    data object Settings : Route

    /**
     * Liste des arbres de la zone que la carte montrait (#229) : le périmètre (centre + rayon)
     * est capturé par « Voir tout » au moment du tap, pour que la liste parle de la même chose
     * que la carte quittée.
     */
    @Serializable
    data class TreeList(
        val latitude: Double,
        val longitude: Double,
        val radiusKm: Double,
        /** Position reelle au moment du « Voir tout », si connue — reference des distances (#260). */
        val userLatitude: Double? = null,
        val userLongitude: Double? = null,
    ) : Route

    /**
     * User profile screen
     */
    @Serializable
    data object Profile : Route

    /**
     * Tree detail screen with specific tree ID
     * @param treeId The unique identifier of the tree to display
     */
    @Serializable
    data class TreeDetail(
        val treeId: Long,
    ) : Route

    /**
     * Harvest detail screen with specific harvest ID
     * @param harvestId The unique identifier of the harvest to display
     */
    @Serializable
    data class HarvestDetail(
        val harvestId: Long,
    ) : Route

    /**
     * User profile screen for a specific user
     * @param userId The unique identifier of the user to display
     */
    @Serializable
    data class UserProfile(
        val userId: Long,
    ) : Route

    /**
     * Design system showcase screen (dev only)
     */
    @Serializable
    data object Showcase : Route

    /**
     * Assistant d'ajout d'arbre. La visée part de la caméra de la carte persistante : la caméra
     * portée par la route ([latitude], [longitude], [zoom]) n'est plus lue par l'assistant
     * (elle reste dans la route pour ne pas changer le contrat de navigation). « Me localiser » est à portée de la
     * visée. [guideRead] : l'aide « Que puis-je recenser ? » vient d'être lue depuis la carte.
     */
    @Serializable
    data class AddTree(
        val latitude: Double? = null,
        val longitude: Double? = null,
        val zoom: Float? = null,
        val origin: AddTreeOrigin = AddTreeOrigin.UNKNOWN,
        val guideRead: Boolean = false,
    ) : Route

    /**
     * Déplacement d'un arbre depuis sa fiche, sur la carte persistante en visée (lot 4). Porte ce
     * que l'écran doit savoir de l'arbre pour ne pas le recharger. Pas de chemin web : écran à
     * état local, comme [AddTree] (cf. RoutePaths).
     */
    @Serializable
    data class MoveTree(
        val treeId: Long,
        val latitude: Double,
        val longitude: Double,
        val speciesName: String,
        val scientificName: String,
        val canReportGone: Boolean,
    ) : Route

    /**
     * Formulaire « Lancer une récolte » (#167), poussé depuis la fiche d'arbre, la carte ou la
     * fiche d'un lieu (ADR 0035). Exactement un de [treeId] et [siteId] est renseigné.
     * @param treeId L'arbre pour lequel la récolte est organisée
     * @param siteId Le lieu de cueillette pour lequel elle l'est
     */
    @Serializable
    data class CreateHarvest(
        val treeId: Long? = null,
        val siteId: Long? = null,
    ) : Route {
        init {
            require((treeId == null) != (siteId == null)) { "CreateHarvest vise un arbre ou un lieu, pas les deux" }
        }

        fun target(): HarvestTarget =
            siteId?.let { HarvestTarget.Site(it) } ?: HarvestTarget.Tree(requireNotNull(treeId))
    }

    /**
     * Écran Activité (#225 P0) : liste paginée des notifications, atteint depuis Profil. Sous-page,
     * pas un onglet (même statut que [MyTrees]/[Settings]).
     */
    @Serializable
    data object Notifications : Route

    /**
     * Préférences de notification (#225 P0, ADR 0017), atteinte depuis [Settings] — première
     * entrée réelle de cet écran jusqu'ici volontairement vide (YAGNI, cf. `SettingsScreen`).
     */
    @Serializable
    data object NotificationPreferences : Route

    /**
     * Formulaire « Donner mon avis » (#437, ADR 0028), poussé depuis le Profil. Pas de chemin web :
     * c'est un formulaire à état local, comme [AddTree] et [CreateHarvest] (cf. RoutePaths).
     * @param sourceScreen Identifiant stable de l'écran d'origine, joint au retour.
     */
    @Serializable
    data class Feedback(
        val sourceScreen: String,
    ) : Route

    /**
     * Écran Frux+ (#456, ADR 0016), poussé depuis le Profil : ce que l'abonnement apporte, l'état et
     * l'échéance. Pas de chemin web, comme [Feedback] (cf. RoutePaths).
     */
    @Serializable
    data object Premium : Route

    /**
     * « Comptes masqués » (#515, ADR 0029), sous-page du Profil : la liste de gestion pour
     * réafficher un compte masqué. Module `feature-blocked-users` (ADR 0027).
     */
    @Serializable
    data object BlockedUsers : Route

    /**
     * Fiche d'un lieu de cueillette (ADR 0035), ouverte à l'anonyme comme la fiche d'arbre. Module
     * `feature-site` (ADR 0027).
     */
    @Serializable
    data class SiteDetail(
        val siteId: Long,
    ) : Route

    /**
     * Création d'un lieu de cueillette (ADR 0035) sur la position épinglée par l'assistant
     * (« Un lieu » à l'étape 1). Pas de chemin web : formulaire à état local, comme [AddTree].
     */
    @Serializable
    data class CreateSite(
        val latitude: Double,
        val longitude: Double,
    ) : Route

    /**
     * Helper property to check if a route requires authentication
     */
    val requiresAuth: Boolean
        get() =
            when (this) {
                // #264 : fiches arbre/récolte ouvertes aux anonymes, l'API les sert déjà sans
                // jeton — les actions qui exigent un compte s'invitent au clic, écran par écran.
                // Settings ouvert aussi (#468) : il porte les pages légales, dues avant tout compte.
                Profile, MyTrees, Favorites, is AddTree, is MoveTree,
                is CreateHarvest, Notifications, NotificationPreferences, is Feedback, Premium,
                BlockedUsers, is CreateSite,
                -> true

                else -> false
            }
}
